// Library methods
import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { useAuth0 } from "@auth0/auth0-react";

// Utilities
import { getPatientById } from "../../../services/Patient";
import {
  getCommentsReport,
  getExamById,
  getSubmisionData,
} from "../../../services/Exam";
import {
  calculateDurationForDisplay,
  isMonocularMulitpleSections,
  isValidColorVisionReport,
  isValidSynoptophoreReport,
  isValidVisualAcuityReport,
  isValidPelliRobsonReport,
  isValidVisualFieldReport,
  isValidCSTumblingEReport,
  isBinocular,
  isValidRAPDReport,
} from "../../../utils/examReportsHelper";
import { sectionCompletionState } from "../../../hooks/useExamStatus";
import { eyeTypes } from "../../../enums";
import useClinic from "../../../hooks/useClinic";

const ReportPdf = (isPdf = false, clinicId, patientId, examId) => {
  // auth
  const { getAccessTokenSilently } = useAuth0();
  const { clinicSettings } = useClinic();

  // navigate
  const navigate = useNavigate();

  const { eyeLeft, eyeRight } = eyeTypes;

  // indicates if the data has been fetched, or if it is still being fetched
  const [isLoading, setIsLoading] = useState(true);

  const [patient, setPatient] = useState({
    sex: "",
    age: "",
    ethinicty: "",
    firstName: "",
    lastName: "",
  });

  const [exam, setExam] = useState({});
  const [reportData, setReportData] = useState({});

  // track if the validation function has already run
  const [hasRunValidation, setHasRunValidation] = useState(false);

  const [shouldDisplayReport, setShouldDisplayReport] = useState(false);
  const [newPatientId, setNewPatientId] = useState(patientId);

  const durationRes = useMemo(() => "00:00", []);
  const patientName = useMemo(
    () => `${patient.firstName} ${patient.lastName}`,
    [patient]
  );

  const getCompletedExamSections = useCallback((exam) => {
    const examSectionsKeys = Object.keys(exam).filter((key) =>
      key.includes("Sections")
    );
    const completedExamSections = examSectionsKeys.filter(
      (key) =>
        exam?.[key]?.length > 0 &&
        exam[key]?.some((examSection) => Boolean(examSection.completionDate))
    );

    return completedExamSections;
  }, []);

  // whether the report should be displayed or not
  const validateExamReports = useCallback(
    (reportToValidate) => {
      const reportSections = Object.values(reportToValidate);
      if (!reportSections.length) return false;

      let reportToEdit = { ...reportToValidate };
      const completedExamSections = getCompletedExamSections(exam);

      completedExamSections.forEach((sectionName) => {
        const report = reportToValidate?.[sectionName]?.examReport;
        const report2 = reportToValidate?.[sectionName]?.examReport2;

        const isInvalidExam =
          exam[sectionName]?.[0]?.completionState ===
            sectionCompletionState.Invalid ||
          exam[sectionName]?.[1]?.completionState ===
            sectionCompletionState.Invalid;

        if (isInvalidExam) {
          // add display property to the report section
          reportToEdit = {
            ...reportToEdit,
            [sectionName]: { ...reportToEdit?.[sectionName], display: false },
          };
          return;
        }

        const isValidColorVision =
          isValidColorVisionReport(report) || isValidColorVisionReport(report2);

        const isValidSynoptophore =
          isValidSynoptophoreReport(report) ||
          isValidSynoptophoreReport(report2);

        const isValidVisualAcuity =
          isValidVisualAcuityReport(report) ||
          isValidVisualAcuityReport(report2);

        const isValidPelliRobson =
          isValidPelliRobsonReport(report) || isValidPelliRobsonReport(report2);

        const isValidCSTumblingE =
          isValidCSTumblingEReport(report) || isValidCSTumblingEReport(report2);

        const isValidVisualField =
          isValidVisualFieldReport(report) || isValidVisualFieldReport(report2);

        const isValidRAPD =
          isValidRAPDReport(report) || isValidRAPDReport(report2);

        if (
          isValidColorVision ||
          isValidSynoptophore ||
          isValidVisualAcuity ||
          isValidPelliRobson ||
          isValidCSTumblingE ||
          isValidVisualField ||
          isValidRAPD
        ) {
          // add display property to the report section
          reportToEdit = {
            ...reportToEdit,
            [sectionName]: { ...reportToEdit?.[sectionName], display: true },
          };
          return;
        }

        // add display property to the report section
        reportToEdit = {
          ...reportToEdit,
          [sectionName]: { ...reportToEdit?.[sectionName], display: false },
        };
        return;
      });

      // update the report data with the new display property
      setReportData({ ...reportToEdit });

      // if at least one section can be displayed, then the report is valid
      const isValidReportData = Object.values(reportToEdit).some(
        (report) => report.display === true
      );
      return isValidReportData;
    },
    [exam, getCompletedExamSections]
  );

  const getComments = useCallback(
    async (token, sectionId, signal) => {
      /**
       * commentData: [{
       *    "content": string,
       *    "conditions": `[string]`,
       * }]
       */
      return getCommentsReport(
        token,
        clinicId,
        newPatientId,
        examId,
        sectionId,
        signal,
        isPdf
      );
    },
    [clinicId, examId, isPdf, newPatientId]
  );

  const fetchReportData = useCallback(
    async (token, sectionId, signal) => {
      try {
        const submission = await getSubmisionData(
          token,
          clinicId,
          newPatientId,
          examId,
          sectionId,
          signal,
          isPdf
        );
        const parsedSubmission = JSON.parse(submission);
        return parsedSubmission;
      } catch (e) {
        return undefined;
      }
    },
    [clinicId, examId, isPdf, newPatientId]
  );

  const fetchSingleExamSection = useCallback(
    async ({ token, signal, examData, sectionName }) => {
      try {
        // check if the first section's eye matches the clinic settings for displaying reports
        const displayEyeFirst =
          Boolean(examData?.[sectionName]?.[0]?.completionDate) &&
          ((examData?.[sectionName]?.[0]?.eye === eyeLeft &&
            Boolean(clinicSettings?.reportDisplayLeftEyeFirst)) ||
            (examData?.[sectionName]?.[0]?.eye === eyeRight &&
              !clinicSettings?.reportDisplayLeftEyeFirst));

        const shouldDoFirstSection =
          !isMonocularMulitpleSections(examData) ||
          isBinocular(examData) ||
          displayEyeFirst;

        const sectionId = shouldDoFirstSection
          ? examData?.[sectionName]?.[0]?.id
          : examData?.[sectionName]?.[1]?.id;

        // get exam report 1
        const examReportContent1 = await fetchReportData(
          token,
          sectionId,
          signal
        );

        if (examReportContent1) {
          const duration = calculateDurationForDisplay(
            examReportContent1?.timing?.elapsed
          );
          const durationStartTime =
            duration?.length > durationRes.length
              ? durationRes + ":00"
              : durationRes;
          const commentData = await getComments(token, sectionId, signal);
          const comments = commentData?.sort((a, b) =>
            b.creationDate.localeCompare(a.creationDate)
          );
          const eyeReport = shouldDoFirstSection
            ? examData?.[sectionName]?.[0]?.eye
            : examData?.[sectionName]?.[1]?.eye;

          setReportData((prev) => ({
            ...prev,
            [sectionName]: {
              ...prev?.[sectionName],
              examReport: examReportContent1,
              commentData,
              comments,
              eyeReport,
              sectionId,
              duration,
              durationStartTime,
            },
          }));
        }

        // get exam report 2, if available
        if (isMonocularMulitpleSections(examData)) {
          const sectionId2 = shouldDoFirstSection
            ? examData?.[sectionName]?.[1]?.id
            : examData?.[sectionName]?.[0]?.id;

          const examReportContent2 = await fetchReportData(
            token,
            sectionId2,
            signal
          );

          if (examReportContent2) {
            const duration2 = calculateDurationForDisplay(
              examReportContent2?.timing?.elapsed
            );
            const durationStartTime2 =
              duration2?.length > durationRes.length
                ? durationRes + ":00"
                : durationRes;
            const commentData2 = await getComments(token, sectionId2, signal);
            const comments2 = commentData2?.sort((a, b) =>
              b.creationDate.localeCompare(a.creationDate)
            );
            const eyeReport2 = shouldDoFirstSection
              ? examData?.[sectionName]?.[1]?.eye
              : examData?.[sectionName]?.[0]?.eye;

            setReportData((prev) => ({
              ...prev,
              [sectionName]: {
                ...prev?.[sectionName],
                examReport2: examReportContent2,
                sectionId2,
                commentData2,
                comments2,
                eyeReport2,
                duration2,
                durationStartTime2,
              },
            }));
          }
        }
      } catch (e) {
        if (isPdf) navigate("/401");
      }
    },
    [
      fetchReportData,
      getComments,
      navigate,
      isPdf,
      durationRes,
      eyeLeft,
      eyeRight,
      clinicSettings?.reportDisplayLeftEyeFirst,
    ]
  );

  // method that fetches the data
  const fetch = useCallback(
    async (signal) => {
      try {
        // get authToken
        let token = null;
        if (!isPdf) token = await getAccessTokenSilently();

        // get the patient and the exam's data
        const fetchPatient = getPatientById(
          token,
          clinicId,
          newPatientId,
          signal,
          isPdf
        );
        const fetchExam = getExamById(
          token,
          clinicId,
          newPatientId,
          examId,
          signal,
          isPdf
        );

        const [patientData, examData] = await Promise.all([
          fetchPatient,
          fetchExam,
        ]);

        // set the patient and exam states
        setPatient(patientData);
        setExam(examData);
        setNewPatientId(examData?.patient?.id);

        const completedExamSections = getCompletedExamSections(examData);

        // fetch exam information for each section
        for (const sectionName of completedExamSections) {
          await fetchSingleExamSection({
            token,
            signal,
            examData,
            sectionName,
          });
        }
      } catch (e) {
        if (isPdf) navigate("/401");
      }
    },
    [
      getCompletedExamSections,
      getAccessTokenSilently,
      fetchSingleExamSection,
      navigate,
      clinicId,
      newPatientId,
      examId,
      isPdf,
    ]
  );

  // method that does the initial data fetching, showing the loading indicator
  const initialFetch = useCallback(
    async (signal) => {
      try {
        setIsLoading(true);
        await fetch(signal);
      } catch (e) {
        //console.log(e);
      } finally {
        setIsLoading(false);
      }
    },
    [fetch]
  );

  const refetchComment = useCallback(
    async (signal) => {
      try {
        let token = null;
        if (!isPdf) token = await getAccessTokenSilently();

        const completedExamSections = getCompletedExamSections(exam);

        // refetch comments for each exam section
        for (const sectionName of completedExamSections) {
          if (reportData?.[sectionName]?.sectionId) {
            const commentData = await getComments(
              token,
              reportData?.[sectionName]?.sectionId,
              signal
            );
            const comments = commentData?.sort((a, b) =>
              b.creationDate.localeCompare(a.creationDate)
            );

            setReportData((prev) => ({
              ...prev,
              [sectionName]: {
                ...prev?.[sectionName],
                commentData,
                comments,
              },
            }));
          }
          if (reportData?.[sectionName]?.sectionId2) {
            const commentData2 = await getComments(
              token,
              reportData?.[sectionName]?.sectionId2,
              signal
            );
            const comments2 = commentData2?.sort((a, b) =>
              b.creationDate.localeCompare(a.creationDate)
            );

            setReportData((prev) => ({
              ...prev,
              [sectionName]: {
                ...prev?.[sectionName],
                commentData2,
                comments2,
              },
            }));
          }
        }
      } catch (e) {
        if (isPdf) navigate("/401");
      }
    },
    [
      getCompletedExamSections,
      getAccessTokenSilently,
      getComments,
      navigate,
      exam,
      isPdf,
      reportData,
    ]
  );

  // initial data fetching
  useEffect(() => {
    const controller = new AbortController();
    initialFetch(controller.signal);
    return () => {
      controller.abort();
    };
  }, [initialFetch]);

  useEffect(() => {
    const fetchedReportData = Object.values(reportData);
    const completedExamSections = getCompletedExamSections(exam);

    // wait until all completed reports are fetched before validating
    // if the validation function has already run, do not run it again
    if (
      fetchedReportData?.length > 0 &&
      completedExamSections?.length > 0 &&
      fetchedReportData?.length === completedExamSections?.length &&
      !hasRunValidation
    ) {
      setShouldDisplayReport(validateExamReports(reportData));
      setHasRunValidation(true);
    }
  }, [
    validateExamReports,
    getCompletedExamSections,
    exam,
    reportData,
    hasRunValidation,
  ]);

  return {
    isLoading,
    patientName,
    patient,
    exam,
    reportData,
    refetchComment,
    shouldDisplayReport,
    newPatientId,
  };
};

export default ReportPdf;
